
import type { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance'
import type { ApiError } from '@empathyco/playboard-core-domain'
import type { PropType } from 'vue'
import type { IndexClientDTO, IndexClientFeedDTO, IndexJobDTO } from '../../models'

import type { IndexJobServiceListFilters } from '../../services/interfaces'

import { computed, defineComponent, getCurrentInstance, onMounted, ref, watch } from 'vue'

import { mapActions, useApiError } from '../../composables'

import { INDEX_CONSTS } from '../../store/index/index.consts'

import { UiAlert, UiButton, UiModal } from '../keen-ui'
import { EpbButton } from '../shared/epb-button'
import { EpbDragAndDrop } from '../shared/epb-drag-and-drop'
import { EpbSelect } from '../shared/epb-select'
import TransitionSize from '../transition-size.component.vue'

interface Option {
  value: string
  label: string
}

export default defineComponent({
  name: 'IndexSubmitJobForm',
  components: {
    EpbSelect,
    EpbDragAndDrop,
    EpbButton,
    UiModal,
    TransitionSize,
    UiAlert,
    UiButton,
  },
  props: {
    instances: {
      type: Array as PropType<Array<Instance>>,
      required: true,
    },
    filters: {
      type: Object as PropType<IndexJobServiceListFilters>,
      required: true,
    },
  },
  setup(props: { instances: Array<Instance>; filters: IndexJobServiceListFilters }, { emit }) {
    const baseClass = 'epb-index-submit-job-form'

    const defaultEmptyOption: Option = {
      value: '',
      label: '',
    }

    const {
      proxy: { $epb, $store },
    } = getCurrentInstance()

    const { getErrorMessage } = useApiError()

    const messages = $epb.messages.tools.index.submitModal

    const { getClient, submitFeed } = mapActions($store, INDEX_CONSTS.moduleName, {
      getClient: INDEX_CONSTS.actions.getClient,
      submitFeed: INDEX_CONSTS.actions.submitFeed,
    })

    const submitModal = ref(null)
    const dragAndDropRef = ref()
    const uploadingFile = ref(false)

    const onOpenModal = (): void => {
      submitModal.value.open()
    }

    const getOptionFromList = (item: Instance & IndexClientFeedDTO): Option => {
      return {
        label: item.prettyName || item.id,
        value: item.id,
      }
    }

    const optionsSort = (option1: Option, option2: Option): number => {
      return option1.label.localeCompare(option2.label)
    }

    const instancesOptions = computed<Array<Option>>(() => {
      return props.instances.map(getOptionFromList).sort(optionsSort)
    })

    const findInstanceOptionSelected = (option: Option): boolean => {
      if (!props.filters.clientIds?.length) {
        return false
      }
      return option.value === props.filters.clientIds[0]
    }

    const instance = ref<IndexClientDTO>()
    const instanceSelected = ref<Option>(
      instancesOptions.value.length === 1
        ? instancesOptions.value[0]
        : instancesOptions.value.find(findInstanceOptionSelected) || defaultEmptyOption,
    )
    const clientFeedSelected = ref<Option>(defaultEmptyOption)
    const fileSelected = ref<File>()

    const clientFeedIdsOptions = computed<Array<Option>>(() => {
      if (!instance.value) {
        return []
      }
      return instance.value.feeds.map(getOptionFromList).sort(optionsSort)
    })

    const validForm = computed<boolean>(() => {
      return !!clientFeedSelected.value.label && !!fileSelected.value
    })

    const fileUploaded = (file: File | undefined): void => {
      fileSelected.value = file
    }

    const onBeforeClose = (): boolean => {
      clientFeedSelected.value = defaultEmptyOption
      dragAndDropRef.value.reset()
      return true
    }

    const onAccept = async (): Promise<void> => {
      uploadingFile.value = true
      try {
        const job: IndexJobDTO = await submitFeed({
          clientId: instanceSelected.value.value,
          feedId:
            clientFeedIdsOptions.value.find(
              option => option.value === clientFeedSelected.value.label,
            )?.value ?? '',
          file: fileSelected.value,
        })
        emit('success', messages.success, true, job)
      } catch (error) {
        emit(
          'error',
          getErrorMessage({
            ...error,
            message: messages.error,
          } as ApiError),
        )
      } finally {
        uploadingFile.value = false
      }
      submitModal?.value?.close()
    }

    const onCancel = (): void => {
      submitModal.value.close()
    }

    const doGetInstance = async (): Promise<void> => {
      if (instanceSelected.value.value) {
        instance.value = await getClient(instanceSelected.value.value)
      }
    }

    onMounted((): void => {
      doGetInstance()
    })

    watch(() => instanceSelected, doGetInstance, { deep: true })

    return {
      baseClass,
      messages,
      submitModal,
      dragAndDropRef,
      instancesOptions,
      instanceSelected,
      clientFeedSelected,
      validForm,
      uploadingFile,
      fileUploaded,
      clientFeedIdsOptions,
      onBeforeClose,
      onAccept,
      onCancel,
      onOpenModal,
    }
  },
})
